import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Typography,
  Tooltip,
  Paper,
  Avatar,
} from '@mui/material';
// The app's real theme (light/dark, with the custom `name` property below) is provided via
// @material-ui/core's ThemeProvider (see App.js) - there is no @mui/material v5 ThemeProvider
// anywhere in the tree, so @mui/material's own useTheme() would just return v5's disconnected
// default theme instead of the app's actual current mode. Must read it from @material-ui/core.
import { useTheme } from '@material-ui/core';
import { useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { patchAPIHandler, postAPIHandler } from 'src/Apiconfig/service';
import { AuthContext } from 'src/context/Auth';
import ChainSelector from 'src/views/assest/ChainSelector';
import { chainLabel, chainIcon } from 'src/views/assest/chainConstants';

// Per-chain breakdown + management for a single MetaMask/DEXEVM wallet - lets the user see
// which of their selected chains synced vs. errored, retry just the failed one(s) instead of
// the whole wallet, and add/remove chains going forward. Reuses ChainSelector (built for the
// add-wallet flow in TabView.js) for the "add another chain" control, since already-selected
// chains here are shown as their own status rows rather than chips.
export default function ManageChainsDialog({ open, onClose, wallet, walletMetrics, onChanged }) {
  const auth = useContext(AuthContext);
  const currency = auth?.userProfileInfo?.currency;
  const symbol = auth?.userProfileInfo?.symbol;

  const [selectedChains, setSelectedChains] = useState([]);
  const [busyChains, setBusyChains] = useState(() => new Set());
  // Tracks which "unknown status" chains we've already auto-resynced this time the dialog was
  // opened, so the effect below fires once per chain instead of looping every time onChanged()
  // refetches data and this component re-renders with the same still-pending chain.
  const autoResyncedRef = useRef(new Set());

  useEffect(() => {
    if (open) {
      setSelectedChains(wallet?.selectedChains || []);
    }
    // Reset per-wallet tracking whenever the dialog closes OR switches to a different wallet
    // (the parent swaps `wallet` directly between cards without `open` ever going false in
    // between) - otherwise a chain name already auto-resynced for one wallet would be silently
    // skipped for a different wallet that happens to share the same chain name.
    autoResyncedRef.current = new Set();
    setBusyChains(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, wallet?.walletId]);

  const setBusy = (chain, isBusy) => {
    setBusyChains((prev) => {
      const next = new Set(prev);
      if (isBusy) next.add(chain);
      else next.delete(chain);
      return next;
    });
  };

  const statusByChain = {};
  (wallet?.chainSyncStatuses || []).forEach((status) => {
    statusByChain[status.source] = status;
  });

  const metricByChain = (chain) => {
    if (!Array.isArray(walletMetrics) || !wallet) return undefined;
    return walletMetrics.find(
      (metric) => metric.walletId === wallet.walletId && metric.source?.toUpperCase() === chain
    );
  };

  const runAction = async (chain, action, apiCall) => {
    setBusy(chain, true);
    try {
      const response = await apiCall();
      if (response?.status === 200) {
        if (action === 'remove') {
          setSelectedChains((prev) => prev.filter((c) => c !== chain));
          toast.success(`${chainLabel(chain)} removed.`);
          // Removal is immediate/definitive - nothing further to wait on.
          setBusy(chain, false);
        } else {
          // The trigger call returning 200 only means the sync was accepted, not that it's
          // done - the actual work keeps running in the backend. Stay "busy" until the
          // resolving effect below sees real status/value data for this chain, rather than
          // clearing the spinner here and risking a stale checkmark in the meantime.
          if (action === 'resync') {
            toast.success(`Resyncing ${chainLabel(chain)}...`);
          } else {
            toast.success(`${chainLabel(chain)} added - syncing now.`);
          }
        }
        onChanged?.();
      } else {
        toast.error(`Unable to ${action} ${chainLabel(chain)}. Please try again.`);
        setBusy(chain, false);
      }
    } catch (e) {
      toast.error(`Unable to ${action} ${chainLabel(chain)}. Please try again.`);
      setBusy(chain, false);
    }
  };

  const handleAdd = (chain) => {
    setSelectedChains((prev) => [...prev, chain]);
    runAction(chain, 'add', () =>
      patchAPIHandler({ endPoint: 'wallet', id: `${wallet.walletId}/chains`, data: { addChains: [chain] } })
    );
  };

  const handleRemove = (chain) => {
    runAction(chain, 'remove', () =>
      patchAPIHandler({ endPoint: 'wallet', id: `${wallet.walletId}/chains`, data: { removeChains: [chain] } })
    );
  };

  const handleRetry = (chain) => {
    runAction(chain, 'resync', () =>
      postAPIHandler({ endPoint: 'wallet', id: `${wallet.walletId}/chains/resync`, data: { chains: [chain] } })
    );
  };

  // A chain the wallet already has selected but with no recorded status and no synced value
  // isn't "not synced" from the user's point of view - the wallet itself may have synced fine,
  // this chain just has no per-chain result yet (e.g. it predates the status-tracking feature,
  // or the last attempt's report never landed). Rather than showing an inert "not synced" label,
  // kick off a resync for it automatically so it resolves to a real status on its own.
  useEffect(() => {
    if (!open || !wallet) return;
    // Read directly from the wallet prop, never the local selectedChains state copy - the
    // state is set in a separate effect and can briefly lag one render behind when the parent
    // switches `wallet` to a different wallet without `open` ever going false in between. Since
    // wallet.walletId is used for the actual API call below, mixing in a stale chain list from
    // the previous wallet would fire a resync for chains that don't belong to this walletId.
    (wallet.selectedChains || []).forEach((chain) => {
      const hasStatus = Boolean(statusByChain[chain]);
      const hasValue = Boolean(metricByChain(chain));
      const alreadyTriggered = autoResyncedRef.current.has(chain);
      if (!hasStatus && !hasValue && !alreadyTriggered && !busyChains.has(chain)) {
        autoResyncedRef.current.add(chain);
        handleRetry(chain);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, wallet]);

  // A trigger call returning 200 only means the sync was accepted, not finished - clear the
  // "busy" spinner only once this chain actually has real status/value data, so the UI never
  // shows a checkmark next to "Syncing..." from stale data in between.
  useEffect(() => {
    if (busyChains.size === 0) return undefined;
    setBusyChains((prev) => {
      let changed = false;
      const next = new Set(prev);
      prev.forEach((chain) => {
        if (statusByChain[chain] || metricByChain(chain)) {
          next.delete(chain);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet, walletMetrics]);

  // While anything is still busy, poll the parent for fresh wallet/metrics data every few
  // seconds so the resolving effect above actually has new data to check against - a resync
  // can take longer than the single onChanged() call fired right after the trigger returns.
  useEffect(() => {
    if (!open || busyChains.size === 0) return undefined;
    const interval = setInterval(() => onChanged?.(), 4000);
    return () => clearInterval(interval);
  }, [open, busyChains.size, onChanged]);

  // Safety net: if a chain has been "busy" for a long time with no resolution (e.g. its status
  // callback silently failed), stop spinning indefinitely and let the user retry manually.
  useEffect(() => {
    if (busyChains.size === 0) return undefined;
    const timers = Array.from(busyChains).map((chain) =>
      setTimeout(() => {
        setBusyChains((prev) => {
          if (!prev.has(chain)) return prev;
          const next = new Set(prev);
          next.delete(chain);
          return next;
        });
        toast(`Still processing ${chainLabel(chain)} - check back in a bit.`);
      }, 60000)
    );
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busyChains]);

  const theme = useTheme();
  const isLight = theme.name === 'LIGHT';
  const cardBg = isLight ? '#FFFFFF' : '#241F2E';
  const cardBorder = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)';
  const subtleText = isLight ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.6)';

  if (!wallet) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: '"Manrope", Helvetica, sans-serif' }}>
        Manage chains — {wallet.aliasName || wallet.name}
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {selectedChains.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No chains selected yet.
          </Typography>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 1.5,
            mb: 1,
          }}
        >
          {selectedChains.map((chain) => {
            const status = statusByChain[chain];
            const metric = metricByChain(chain);
            const isFailed = status?.status === 'COMPLETED_WITH_ERRORS';
            const isBusy = busyChains.has(chain);
            const icon = chainIcon(chain);
            const resolved = status || metric;

            const statusNode = isBusy ? (
              <CircularProgress size={14} thickness={5} />
            ) : isFailed ? (
              <Tooltip title={status?.errorMessage || 'Sync failed'}>
                <ErrorIcon color="error" sx={{ fontSize: 16 }} />
              </Tooltip>
            ) : resolved ? (
              <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
            ) : null;

            const valueLine = isBusy
              ? 'Syncing...'
              : isFailed
                ? 'Sync failed'
                : resolved
                  // A chain can be genuinely synced with zero balance/transactions (no
                  // walletMetrics row exists for it at all in that case) - default to $0/0
                  // txns instead of falling back to a bare timestamp, so every resolved chain
                  // reads the same way regardless of whether it has data.
                  ? `${symbol}${new Intl.NumberFormat().format((metric?.assetTotal?.[currency] ?? 0).toFixed(2))} · ${metric?.transactionCount ?? 0} txns`
                  : 'Syncing...';

            return (
              <Paper
                key={chain}
                elevation={0}
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  p: 1.75,
                  borderRadius: '12px',
                  backgroundColor: cardBg,
                  border: `1px solid ${cardBorder}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  transition: 'box-shadow 0.15s ease',
                  '&:hover': { boxShadow: '0px 4px 14px rgba(0,0,0,0.12)' },
                }}
              >
                  {/* Top row: icon + name on the left, resync/remove on the right - mirrors the
                      main wallet card's header layout so this reads as a smaller version of the
                      same card, not a different pattern. */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                      <Avatar
                        src={icon || undefined}
                        variant="circular"
                        sx={{
                          width: 26,
                          height: 26,
                          flexShrink: 0,
                          backgroundColor: icon ? 'transparent' : '#5A5A5A',
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        {!icon && chainLabel(chain).charAt(0)}
                      </Avatar>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ fontWeight: 600, fontFamily: '"Manrope", Helvetica, sans-serif' }}
                      >
                        {chainLabel(chain)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ml: 0.5 }}>
                      <IconButton size="small" onClick={() => handleRetry(chain)} disabled={isBusy} title="Resync this chain" sx={{ p: 0.5 }}>
                        <ReplayIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleRemove(chain)} disabled={isBusy} title="Remove this chain" sx={{ p: 0.5 }}>
                        <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: '"Manrope", Helvetica, sans-serif' }}>
                      {isFailed ? '—' : resolved ? `${symbol}${new Intl.NumberFormat().format((metric?.assetTotal?.[currency] ?? 0).toFixed(2))}` : '—'}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {statusNode}
                      <Typography variant="caption" sx={{ color: subtleText }}>
                        {isFailed || isBusy ? valueLine : `${metric?.transactionCount ?? 0} txns`}
                      </Typography>
                    </Box>
                  </Box>
              </Paper>
            );
          })}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Add another chain
          </Typography>
          {/* selectedChains (not []) so ChainSelector actually filters out chains this
              wallet already has - passing [] here meant every supported chain, including
              ones already added, showed up as an addable option. */}
          <ChainSelector chains={selectedChains} onAdd={handleAdd} onRemove={() => {}} />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
